import { ModalContainer } from 'components/Modals/ModalContainer';
import React, { useCallback, useMemo, useState } from 'react';
import { Box, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { formatEmailTemplate } from 'modules/analyse/utils/formatEmailTemplate';
import { Input } from 'components/Inputs';
import { AutomationActionType } from 'generated-graphql/types';
import { Checkbox } from 'common/form';
import { useGetAutomationTaskEmailTemplateByOrganizationQuery } from 'generated-graphql/hooks/apollo-hooks';
import { EmailTemplate } from 'modules/analyse/types/EmailTemplate';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { Visibility } from '@mui/icons-material';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';

const container: SxProps = {
  m: 6
};

const bodyContainer: SxProps = {
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'customGrey.medium',
  backgroundColor: 'customGrey.light',
  p: 2
};

const buttonsContainer: SxProps = {
  display: 'flex',
  gap: 1,
  mt: 2
};

type Props = {
  index: number;
  analysisAutomationItem: AutomationFormItem;
  automationActionType: AutomationActionType;
  setFieldValue: (field: string, value: any) => void;
};

export const AutomationMessagePreview: React.FC<Props> = ({
  index,
  automationActionType,
  analysisAutomationItem,
  setFieldValue
}) => {
  const organization = useOrganization();
  const [includeJobBoardLink, setIncludeJobBoardLink] = useState<boolean>(
    analysisAutomationItem.action.includeJobBoardLink || false
  );
  const [includePersonalisation, setIncludePersonalisation] = useState<boolean>(
    analysisAutomationItem.action.includePersonalisation || false
  );
  const [jobBoardLink, setJobBoardLink] = useState<string | undefined>(
    analysisAutomationItem.action.jobBoardLink ?? undefined
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [emailTemplate, setEmailTemplate] = useState<
    EmailTemplate | undefined
  >();

  useGetAutomationTaskEmailTemplateByOrganizationQuery({
    variables: {
      organizationId: organization.id
    },
    skip: !organization.id,
    onCompleted: (data) => {
      const emailTemplate = extractQueryListItems(data)[0];

      if (emailTemplate) {
        setEmailTemplate(emailTemplate);
        setFieldValue(
          `${index}.action.automationTaskEmailTemplateId`,
          emailTemplate.id
        );
      }
    }
  });

  const emailTemplateToDisplay = useMemo(() => {
    if (
      automationActionType === AutomationActionType.SendJobRecommendationEmail
    ) {
      if (includeJobBoardLink) {
        return emailTemplate?.jobRecommendationsEmailTemplateWithJobBoardLink;
      }
      return emailTemplate?.jobRecommendationsEmailTemplate;
    }

    if (includePersonalisation && includeJobBoardLink) {
      return emailTemplate?.emailTemplateWithPersonalisationAndJobBoardLink;
    }

    if (includePersonalisation) {
      return emailTemplate?.emailTemplateWithPersonalisation;
    }

    if (includeJobBoardLink) {
      return emailTemplate?.emailTemplateWithJobBoardLink;
    }

    return emailTemplate?.defaultEmailTemplate;
  }, [
    includePersonalisation,
    includeJobBoardLink,
    emailTemplate?.defaultEmailTemplate,
    emailTemplate?.emailTemplateWithPersonalisationAndJobBoardLink,
    emailTemplate?.emailTemplateWithPersonalisation,
    emailTemplate?.emailTemplateWithJobBoardLink,
    emailTemplate?.jobRecommendationsEmailTemplate,
    emailTemplate?.jobRecommendationsEmailTemplateWithJobBoardLink,
    automationActionType
  ]);

  const resetValues = useCallback(() => {
    setIncludeJobBoardLink(!!analysisAutomationItem.action.includeJobBoardLink);
    setIncludePersonalisation(
      !!analysisAutomationItem.action.includePersonalisation
    );

    const jobBoardLink = analysisAutomationItem.action.jobBoardLink;

    if (jobBoardLink) {
      setJobBoardLink(jobBoardLink);
    }
  }, [analysisAutomationItem, index]);

  const resetValuesAndClose = () => {
    resetValues();
    setIsModalOpen(false);
  };

  const saveValues = () => {
    setFieldValue(`${index}.action.includeJobBoardLink`, includeJobBoardLink);
    setFieldValue(
      `${index}.action.includePersonalisation`,
      includePersonalisation
    );
    setFieldValue(`${index}.action.jobBoardLink`, jobBoardLink);
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        variant="tertiary"
        endIcon={<Visibility />}
      >
        Preview message
      </Button>
      <ModalContainer
        title="Email preview"
        onClose={() => setIsModalOpen(false)}
        open={isModalOpen}
        containerSx={container}
      >
        <Box>
          <Typography mb={2}>
            Subject: Your application to the [role] role at [company]
          </Typography>

          <Box sx={bodyContainer}>
            {emailTemplateToDisplay && (
              <Typography whiteSpace="pre-line">
                {formatEmailTemplate(emailTemplateToDisplay)}
              </Typography>
            )}
          </Box>

          <Typography mt={2} whiteSpace="pre-line">
            The information in brackets will be completed for each applicant by
            Popp
          </Typography>

          <Box mt={3}>
            {automationActionType === AutomationActionType.SendEmail && (
              <Checkbox
                field={{
                  checkboxLabel: 'Include personalised feedback'
                }}
                onChange={(value) => {
                  setIncludePersonalisation(value);
                }}
                value={includePersonalisation}
              />
            )}

            <Checkbox
              field={{
                checkboxLabel: 'Include job board'
              }}
              onChange={(value) => {
                setIncludeJobBoardLink(value);

                if (!value) {
                  setJobBoardLink(undefined);
                }
              }}
              value={includeJobBoardLink}
            />
          </Box>

          {includeJobBoardLink && (
            <Box width="40%" sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="customGrey.dark">
                Add a hyperlink to your job board including http://
              </Typography>
              <Input
                placeholder="Job board URL"
                onChange={(e) => {
                  setJobBoardLink(e.target.value);
                }}
                id={`${index}.action.jobBoardLink`}
                value={jobBoardLink}
                name={`${index}.action.jobBoardLink`}
                type="url"
              />
            </Box>
          )}

          <Box sx={buttonsContainer}>
            <Button variant="primary" onClick={saveValues}>
              Save
            </Button>
            <Button variant="secondary" onClick={resetValuesAndClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </ModalContainer>
    </>
  );
};
